/**
 * Scroll to Anchor
 * Gestisce gli anchor link (voci di menu e pulsanti .btn-scroll-anchor)
 * tenendo conto di navbar sticky e admin bar.
 *
 * - Stessa pagina: scroll fluido con offset.
 * - Altra pagina: redirect alla pagina corretta + #ancora (lo scroll
 *   avviene al load leggendo l'hash).
 */

const DEFAULT_OPTIONS = {
  navbarId: "mainNavbar",
  adminBarId: "wpadminbar",
  extraOffset: 40,
};

// Ancore hash limitate al wrapper della navbar (#mainNavbar contiene sia il menu
// desktop sia l'offcanvas mobile): evita di dirottare ancore di altri componenti
// (es. <a href="#tab-x" data-bs-toggle="tab"> di Bootstrap). I pulsanti
// .btn-scroll-anchor restano globali perché vivono nel contenuto della pagina.
const HASH_LINK_SELECTOR = [
  '#mainNavbar a[href*="#"]:not([href="#"]):not([href="#!"])',
  ".btn-scroll-anchor[data-target]",
].join(", ");

// --- Helpers --------------------------------------------------------------

function decodeHash(hash) {
  if (!hash) return "";
  try {
    return decodeURIComponent(hash.replace(/^#/, ""));
  } catch {
    return hash.replace(/^#/, "");
  }
}

function findTarget(id) {
  if (!id) return null;
  return document.getElementById(id) || document.getElementsByName(id)[0] || null;
}

function normalizePath(pathname) {
  const path = pathname || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

function isSamePageUrl(url) {
  return (
    url.origin === window.location.origin &&
    normalizePath(url.pathname) === normalizePath(window.location.pathname) &&
    url.search === window.location.search
  );
}

function isRealUrl(href) {
  return Boolean(href) && href !== "#" && !href.startsWith("#");
}

// --- Offset & scroll ------------------------------------------------------

function getFixedOffset(options) {
  const navbar = document.getElementById(options.navbarId);
  const adminBar = document.getElementById(options.adminBarId);
  return (navbar?.offsetHeight || 0) + (adminBar?.offsetHeight || 0) + options.extraOffset;
}

function getTargetTop(element, options) {
  return Math.max(0, element.getBoundingClientRect().top + window.scrollY - getFixedOffset(options));
}

function focusTarget(element) {
  // Focus per accessibilità, senza ri-scrollare.
  if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1");
  element.focus({ preventScroll: true });
}

/**
 * Rete di sicurezza: se il layout si sposta durante l'animazione smooth
 * (es. immagini lazy che caricano sopra il target) lo scroll può atterrare
 * impreciso. Quando lo scroll si è fermato ri-misuriamo nello stato assestato
 * e, se serve, correggiamo la posizione.
 */
function correctAfterScroll(element, options) {
  let fallbackTimer;

  const correct = () => {
    window.clearTimeout(fallbackTimer);
    window.removeEventListener("scrollend", correct);

    const finalTop = getTargetTop(element, options);
    if (Math.abs(window.scrollY - finalTop) > 1) {
      // "instant" forza lo snap anche con scroll-behavior: smooth su html.
      window.scrollTo({ top: finalTop, behavior: "instant" });
    }
  };

  window.addEventListener("scrollend", correct, { once: true });
  fallbackTimer = window.setTimeout(correct, 700); // fallback: browser senza 'scrollend'
}

function scrollToElement(element, options, behavior = "smooth") {
  window.scrollTo({ top: getTargetTop(element, options), behavior });
  focusTarget(element);

  if (behavior === "smooth") correctAfterScroll(element, options);
}

// --- Risoluzione del target dal trigger -----------------------------------

/**
 * Per le sottovoci con solo #ancora (es. "#documenti") risale al link
 * genitore della dropdown per ottenere l'URL della pagina di destinazione.
 */
function getDropdownParentUrl(link) {
  const menu = link.closest(".dropdown-menu");
  const dropdown = menu ? menu.closest(".dropdown") : link.closest(".dropdown");
  if (!dropdown) return "";

  // Link genitore della dropdown (es. "Articolo 14"): è il .dropdown-toggle.
  const toggleHref = dropdown
    .querySelector(":scope > a.dropdown-toggle, :scope > a.nav-link")
    ?.getAttribute("href");
  if (isRealUrl(toggleHref)) return toggleHref;

  // Fallback: prima sottovoce con URL reale (es. "Cos'è").
  const firstItemHref = menu
    ?.querySelector('a[href]:not([href^="#"])')
    ?.getAttribute("href");
  return isRealUrl(firstItemHref) ? firstItemHref : "";
}

/**
 * Restituisce { targetId, pageUrl, sameDoc } dal trigger cliccato, oppure null.
 * - sameDoc: il trigger punta al documento corrente (ancora pura, pulsante, o
 *   URL della pagina corrente) → se il target esiste qui si scrolla direttamente.
 * - pageUrl: pagina di destinazione, usata come fallback quando il target NON è
 *   presente nella pagina corrente.
 */
function getTriggerData(trigger) {
  // Pulsante .btn-scroll-anchor con data-target / data-url.
  if (trigger.classList.contains("btn-scroll-anchor")) {
    const targetId = trigger.dataset.target || "";
    if (!targetId) return null;
    return { targetId, pageUrl: trigger.dataset.url || "", sameDoc: true };
  }

  // Anchor link (voci di menu).
  const rawHref = trigger.getAttribute("href");
  if (!rawHref || !rawHref.includes("#")) return null;

  const url = new URL(rawHref, window.location.href);
  const targetId = decodeHash(url.hash);
  if (!targetId) return null;

  // Solo #ancora (es. "#documenti") → riferito a questo documento; pageUrl è il
  // fallback (pagina genitore della dropdown) per quando non siamo lì.
  if (rawHref.startsWith("#")) {
    return { targetId, pageUrl: getDropdownParentUrl(trigger), sameDoc: true };
  }

  // URL completo → stesso documento solo se l'URL è la pagina corrente.
  const sameDoc = isSamePageUrl(url);
  return {
    targetId,
    pageUrl: sameDoc ? "" : url.origin + url.pathname + url.search,
    sameDoc,
  };
}

// --- Chiusura menu aperti -------------------------------------------------

function closeOpenNavigation(link) {
  const bootstrap = window.bootstrap;
  if (!bootstrap) return;

  const offcanvas = link.closest(".offcanvas.show");
  if (offcanvas) bootstrap.Offcanvas.getInstance(offcanvas)?.hide();

  const toggle = link.closest(".dropdown")?.querySelector('[data-bs-toggle="dropdown"]');
  if (toggle) bootstrap.Dropdown.getInstance(toggle)?.hide();
}

// --- Handlers -------------------------------------------------------------

function handleAnchorClick(event, options) {
  if (event.defaultPrevented) return;

  const trigger = event.target.closest(HASH_LINK_SELECTOR);
  if (!trigger) return;

  const data = getTriggerData(trigger);
  if (!data) return;

  const target = findTarget(data.targetId);

  // Stessa pagina: il target esiste qui ed è riferito a questo documento.
  if (target && data.sameDoc) {
    event.preventDefault();
    closeOpenNavigation(trigger);
    // encodeURIComponent: scrittura dell'hash simmetrica a decodeHash (lettura),
    // così id con accenti/spazi/caratteri speciali restano URL validi e rileggibili.
    history.pushState(null, "", `#${encodeURIComponent(data.targetId)}`);
    scrollToElement(target, options);
    return;
  }

  // Altra pagina: redirect alla pagina corretta + #ancora.
  if (data.pageUrl) {
    event.preventDefault();
    const url = new URL(data.pageUrl, window.location.href);
    url.hash = data.targetId;
    window.location.href = url.toString();
  }
}

function scrollToHashOnLoad(options) {
  const target = findTarget(decodeHash(window.location.hash));
  if (!target) return;

  const run = () => scrollToElement(target, options, "auto");
  requestAnimationFrame(run);
  // Ricalcola dopo il load completo (immagini/font possono spostare il layout).
  // { once: true }: il listener si auto-rimuove dopo l'unico 'load'.
  window.addEventListener("load", run, { once: true });
}

// --- Init -----------------------------------------------------------------

export function initScrollAnchor(customOptions = {}) {
  const options = { ...DEFAULT_OPTIONS, ...customOptions };

  document.addEventListener("click", (event) => handleAnchorClick(event, options));
  scrollToHashOnLoad(options);
}
